/**
 * JudgeShell - Minimal layout for non-admin users on judge interface.
 *
 * No sidebar, no topbar - just a simple header with logout and full content area.
 * Optimized for mobile use by referees in the field.
 */

import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/Brightness4';
import LightModeIcon from '@mui/icons-material/Brightness7';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

export function JudgeShell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { themeMode, toggleTheme } = useUIStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Minimal header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {user?.first_name || user?.username || 'Tuomari'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={`${themeMode === 'light' ? 'Tumma' : 'Vaalea'} teema`}>
            <IconButton size="small" onClick={toggleTheme}>
              {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Kirjaudu ulos">
            <IconButton size="small" onClick={handleLogout} color="error">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

